/**
 * bootUrl — the address this page booted with, readable for the page's
 * whole life.
 *
 * On the main game domain the address bar is cleaned once the game scene
 * is ready (mainGamePlayUrl.ts cleanMainGameAddressBar rewrites it to `/play`), so
 * `window.location` stops carrying the boot query. Every client reader of
 * the page's query or path goes through these accessors instead: before the
 * clean they return the live location (so boot-time rewrites such as
 * coerceModeUrl are seen), after it they return the frozen pre-clean value.
 *
 * Imports only the leaf inGameNavigation.ts, so the first boot import can use it.
 * tests/architecture/no-raw-location-read.spec.ts keeps raw
 * `location.search` / `location.pathname` / `location.href` reads out of
 * the client tree.
 */

import { TRUSTED_BOOT_STATE_KEY } from './inGameNavigation';

/** history.state key that carries the pre-clean address across a reload. */
export const BOOT_URL_STATE_KEY = 'h2kBootUrl';

interface BootUrlSnapshot {
  pathname: string;
  search: string;
  hash: string;
}

type LocationLike = {
  pathname?: string;
  search?: string;
  hash?: string;
  origin?: string;
};

type HistoryLike = {
  state: unknown;
  replaceState: (data: unknown, unused: string, url?: string | URL | null) => void;
};

let frozen: BootUrlSnapshot | null = null;

function liveLocation(): LocationLike | undefined {
  return (globalThis as { window?: { location?: LocationLike } }).window?.location;
}

function liveHistory(): HistoryLike | undefined {
  return (globalThis as { window?: { history?: HistoryLike } }).window?.history;
}

function current(): BootUrlSnapshot {
  if (frozen) return frozen;
  const loc = liveLocation();
  return { pathname: loc?.pathname ?? '', search: loc?.search ?? '', hash: loc?.hash ?? '' };
}

/** The boot query string, `?`-prefixed like `location.search` (or ''). */
export function bootSearch(): string {
  return current().search;
}

/** The boot path, like `location.pathname`. */
export function bootPathname(): string {
  return current().pathname;
}

/** Absolute boot URL, like `location.href`. */
export function bootHref(): string {
  const c = current();
  const origin = liveLocation()?.origin ?? '';
  return `${origin}${c.pathname}${c.search}${c.hash}`;
}

/** A fresh, mutable copy of the boot query. */
export function bootSearchParams(): URLSearchParams {
  return new URLSearchParams(current().search);
}

/** One boot query value, or null. */
export function bootParam(key: string): string | null {
  return bootSearchParams().get(key);
}

/** True once the address bar has been cleaned for this page. */
export function isBootUrlFrozen(): boolean {
  return frozen !== null;
}

/**
 * Snapshot the live address as this page's boot URL. Idempotent: the first
 * snapshot wins, so a second clean never freezes the already-clean path.
 */
export function freezeBootUrl(): string {
  if (!frozen) {
    const loc = liveLocation();
    frozen = { pathname: loc?.pathname ?? '/', search: loc?.search ?? '', hash: loc?.hash ?? '' };
  }
  return `${frozen.pathname}${frozen.search}${frozen.hash}`;
}

/**
 * The game rewriting its own address keeps that entry trusted (D44,
 * inGameNavigation.ts): helix2000.com honours a reload of an entry whose
 * recorded trust names its current `path + search`, so the record follows the
 * rewrite. An entry that was not trusted stays untrusted.
 */
function carryTrust(state: unknown, before: string, after: string): unknown {
  if (!state || typeof state !== 'object') return state;
  const record = state as Record<string, unknown>;
  return record[TRUSTED_BOOT_STATE_KEY] === before ? { ...record, [TRUSTED_BOOT_STATE_KEY]: after } : state;
}

/**
 * Change a query value for the rest of this page (and its reload). Before the
 * clean the live address bar is rewritten, exactly like the
 * `new URL(location.href)` + `replaceState` it replaces. After the clean the
 * frozen boot query and its reload stash change; the visible address stays
 * clean.
 */
export function updateBootSearchParams(mutate: (params: URLSearchParams) => void): void {
  const history = liveHistory();
  if (!frozen) {
    const loc = liveLocation();
    if (!loc || !history) return;
    const params = new URLSearchParams(loc.search ?? '');
    mutate(params);
    const q = params.toString();
    const before = `${loc.pathname ?? '/'}${loc.search ?? ''}`;
    const after = `${loc.pathname ?? '/'}${q ? `?${q}` : ''}`;
    history.replaceState(carryTrust(history.state, before, after), '', `${after}${loc.hash ?? ''}`);
    return;
  }
  const params = new URLSearchParams(frozen.search);
  mutate(params);
  const q = params.toString();
  frozen = { ...frozen, search: q ? `?${q}` : '' };
  if (!history) return;
  const state = history.state && typeof history.state === 'object' ? history.state : {};
  history.replaceState(
    { ...(state as Record<string, unknown>), [BOOT_URL_STATE_KEY]: `${frozen.pathname}${frozen.search}${frozen.hash}` },
    '',
  );
}

/**
 * Make `pathAndSearch` (`/?map=…`) this page's boot URL and push a history
 * entry for it — an in-place map trip (plan 25) that replaces a page
 * navigation. Before the clean the address bar shows it; after the clean the
 * bar stays clean and the new entry's stash carries it, so a reload or a Back
 * that reloads lands on the right map either way.
 */
export function pushBootUrl(pathAndSearch: string): void {
  const history = liveHistory() as (HistoryLike & {
    pushState?: (data: unknown, unused: string, url?: string | URL | null) => void;
  }) | undefined;
  const parsed = new URL(pathAndSearch, 'http://boot.invalid');
  const next: BootUrlSnapshot = { pathname: parsed.pathname, search: parsed.search, hash: parsed.hash };
  const state = history?.state && typeof history.state === 'object'
    ? (history.state as Record<string, unknown>)
    : {};
  if (!frozen) {
    // The game pushed this entry itself: a trusted page's trust names the new
    // address, not the one it left (plan 26 F11 — a stale record sent a
    // pre-clean reload of the pushed entry to the roster).
    const pushed = Object.prototype.hasOwnProperty.call(state, TRUSTED_BOOT_STATE_KEY)
      ? { ...state, [TRUSTED_BOOT_STATE_KEY]: `${next.pathname}${next.search}` }
      : state;
    history?.pushState?.(pushed, '', `${next.pathname}${next.search}${next.hash}`);
    return;
  }
  frozen = next;
  history?.pushState?.({ ...state, [BOOT_URL_STATE_KEY]: `${next.pathname}${next.search}${next.hash}` }, '');
}

/** Test seam: forget the frozen snapshot. */
export function resetBootUrlForTests(): void {
  frozen = null;
}
